/**
 * D-Brief: the exception brief, and the question layer over the evidence.
 *
 * Not another dashboard. One page, three things: what will degrade capacity
 * in the next thirty days, with the confidence, the cost in closure minutes
 * and remote-stand penalties, and the decision to make this week and who
 * owns it. Under it, a question box that answers "why did we close runway
 * 17/35 for six hours last Tuesday and what did it cost?" from the closure
 * record, the inspection, the work order, its photographs and the clause the
 * rule carries.
 *
 * Both halves are built from the intelligence report's evidence base and
 * the platform's closure and capacity records, and both are pure: the page
 * gathers, this module decides. Where a figure is not on record — a zone
 * with no closure history, a window the capacity model cannot cover — the
 * brief says so in the cell rather than filling it.
 */
import type { Alert, AlertExplanation } from "./alerts";
import type { ClosureCost } from "./capacity";
import type { Closure } from "./closures";
import type { Rule } from "./compliance";
import type { Inspection } from "./inspections";
import { money, type Evidence, type Finding, type IntelligenceReport } from "./intelligence";
import { isOpen, type WorkOrder } from "./workOrders";
import { locale, t, tn } from "../i18n";

const DAY = 86_400_000;

const localDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* ── the brief ────────────────────────────────────────────────────────── */

export interface BriefItem {
  rank: number;
  findingId: string;
  headline: string;
  zone?: string;
  horizon: string;
  horizonDays?: number;
  confidence: { level: "high" | "medium" | "low"; pct?: number; why: string };
  closureMinutes: { value?: number; basis: string; evidence: Evidence[] };
  remoteStand: { bussed?: number; stranded?: number; movements?: number; basis: string };
  decision: { what: string; by: string; owner: string; ownerKnown: boolean };
  evidence: Evidence[];
}

export interface Brief {
  date: string;
  generatedAt: string;
  siteCode: string;
  items: BriefItem[];
  /** how many findings were weighed for the three */
  considered: number;
  notes: string[];
}

export interface BriefContext {
  now: Date;
  closures: Closure[];
  /** closure cost per zone, for the window `briefWindowFor` gives; null where the model could not answer */
  closureCosts: Record<string, ClosureCost | null>;
  workOrders: WorkOrder[];
}

const CAPACITY = /\b(RWY|TWY|APRON|APN|STAND|GATE|PBB|ST-)/i;
const CAPACITY_WORDS = /\b(runway|taxiway|stand|apron|bridge|holding[- ]position|threshold|touchdown)\b/i;

/** Weight by what a closure of it does to the operation. */
function capacityWeight(f: Finding): number {
  const text = `${f.where ?? ""} ${f.headline}`;
  if (/\bRWY|runway|threshold|touchdown/i.test(text)) return 3;
  if (/\bTWY|taxiway|holding/i.test(text)) return 2;
  if (CAPACITY.test(text) || CAPACITY_WORDS.test(text)) return 1;
  return 0;
}

/** The findings the brief weighs, most pressing first. */
export function capacityCandidates(report: IntelligenceReport): Finding[] {
  /* an alert and the overdue work order it raised are one problem, not two:
     the alert row carries the rule and the reference, so it is the one kept */
  const workCitedByAlerts = new Set(
    report.findings
      .filter((f) => f.id.startsWith("alert:"))
      .flatMap((f) => f.attribution.evidence.filter((e) => e.kind === "work_order").map((e) => e.ref)),
  );
  return report.findings
    .filter((f) => !(f.id.startsWith("sla:") && f.prediction.evidence.some((e) => workCitedByAlerts.has(e.ref))))
    .filter((f) => capacityWeight(f) > 0)
    .filter((f) => (f.phase === "ahead" ? f.horizon.days != null && f.horizon.days <= 30 : f.severity <= 2))
    .map((f) => {
      const immediacy = f.phase === "now" ? 1 : Math.max(0.2, 1 - (f.horizon.days ?? 30) / 30);
      return { f, score: capacityWeight(f) * (6 - f.severity) * immediacy };
    })
    .sort((a, b) => b.score - a.score)
    .map((x) => x.f);
}

/** The zone a finding is about, as a code the closure and capacity records use. */
export function zoneOf(f: Finding): string | undefined {
  const m = (f.where ?? "").match(/\b(RWY-[\w-]+|TWY-[\w-]+|APRON-\w+|STAND-\w+|[A-Z]\d{1,2})\b/);
  if (m) return m[1];
  const h = f.headline.match(/\bRWY\s*(\d{2})\s*\/\s*(\d{2})/i);
  if (h) return `RWY-${h[1]}-${h[2]}`;
  return undefined;
}

/** "A12" and "STAND-A12" are the same place; the records are not consistent about the prefix. */
export function sameZone(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const x = a.toUpperCase();
  const y = b.toUpperCase();
  return x === y || x.endsWith(`-${y}`) || y.endsWith(`-${x}`);
}

function median(xs: number[]): number | undefined {
  if (xs.length === 0) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

const closureEvidence = (c: Closure): Evidence => ({
  kind: "hold_point",
  ref: c.reference ?? c.id.slice(0, 8),
  label: `${c.zoneCode ?? "?"} ${c.kind} ${c.extent} closure — ${c.reason}`,
  detail: `${c.startedAt.slice(0, 16).replace("T", " ")} · ${c.minutes ?? "?"} min`,
});

/**
 * The window to price a closure of the zone in: tomorrow, at the hour the
 * zone was last closed, for as long as it usually is. A guess, and named as
 * one in the cell — the alternative is not pricing it at all.
 */
export function briefWindowFor(
  zone: string,
  closures: Closure[],
  now: Date,
): { from: string; to: string; minutes: number } {
  const past = closures.filter((c) => sameZone(c.zoneCode, zone) && c.minutes != null);
  const minutes = median(past.map((c) => c.minutes as number)) ?? 120;
  const latest = past.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
  const start = new Date(now.getTime() + DAY);
  const hour = latest ? new Date(latest.startedAt).getHours() : 9;
  start.setHours(hour, 0, 0, 0);
  return { from: start.toISOString(), to: new Date(start.getTime() + minutes * 60_000).toISOString(), minutes };
}

/** This working week's last day, Thursday, as the platform's operator keeps a Sunday-to-Thursday week. */
function endOfWeek(now: Date): string {
  const d = new Date(now);
  const to = (4 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + (to === 0 && d.getHours() >= 12 ? 7 : to));
  return d.toISOString().slice(0, 10);
}

function decisionFor(f: Finding, work: WorkOrder[], now: Date): BriefItem["decision"] {
  const by = t("by Thursday {date}", { date: endOfWeek(now) });
  const open = work.filter((w) => isOpen(w.status));
  const w = open[0];
  const owner = f.attribution.party;
  if (f.id.startsWith("test:")) {
    return {
      what: t("Commission the repair and order the retest — {finding}.", { finding: f.headline.toLowerCase() }),
      by,
      owner: owner ?? t("Testing owner (unassigned)"),
      ownerKnown: !!owner,
    };
  }
  if (f.id.startsWith("obligation:")) {
    return {
      what: t("Assign the obligation and file the evidence against it."),
      by,
      owner: owner ?? t("Compliance owner (unassigned)"),
      ownerKnown: !!owner,
    };
  }
  if (f.id.startsWith("hold:")) {
    return {
      what: t("Decide whether the hold point is re-inspected or the project re-sequenced."),
      by,
      owner: owner ?? t("Project owner"),
      ownerKnown: !!owner,
    };
  }
  if (!w) {
    return {
      what: t("Create the work order and name a crew for it."),
      by,
      owner: owner ?? t("Maintenance lead (unassigned)"),
      ownerKnown: !!owner,
    };
  }
  if (!w.crew) {
    return {
      what: t("Assign a crew to {number} and set its due date.", { number: w.number }),
      by,
      owner: owner ?? (w.discipline ? t("{discipline} lead", { discipline: w.discipline }) : t("Maintenance lead")),
      ownerKnown: !!owner,
    };
  }
  const overdue = w.dueAt && new Date(w.dueAt).getTime() < now.getTime();
  if (overdue) {
    return {
      what: t("Escalate {number} or extend its SLA with a recorded reason — it is past due.", { number: w.number }),
      by: t("today"),
      owner: w.crew,
      ownerKnown: true,
    };
  }
  if (f.phase === "ahead") {
    return {
      what: t("Book the closure window for {number} in When to work, before the forecast date.", { number: w.number }),
      by,
      owner: w.crew,
      ownerKnown: true,
    };
  }
  return {
    what: t("Confirm {number} is resourced to complete by its due date.", { number: w.number }),
    by,
    owner: w.crew,
    ownerKnown: true,
  };
}

export function buildBrief(report: IntelligenceReport, ctx: BriefContext): Brief {
  const candidates = capacityCandidates(report);
  const byNumber = new Map(ctx.workOrders.map((w) => [w.number, w]));
  const items: BriefItem[] = candidates.slice(0, 3).map((f, i) => {
    const work = f.attribution.evidence
      .filter((e) => e.kind === "work_order")
      .map((e) => byNumber.get(e.ref))
      .filter((w): w is WorkOrder => !!w);
    /* the finding's own zone, else the zone of the work on it */
    const zone = zoneOf(f) ?? work.find((w) => w.zoneCode)?.zoneCode;

    /* closure minutes: what this zone's closures have taken, else what the work plans */
    const history = ctx.closures.filter((c) => sameZone(c.zoneCode, zone) && c.minutes != null);
    const med = median(history.map((c) => c.minutes as number));
    const labour = work.find((w) => w.labourHours != null)?.labourHours;
    const closureMinutes: BriefItem["closureMinutes"] =
      med != null
        ? {
            value: med,
            basis: t("median of {closures} of {zone} on record", {
              closures: tn(history.length, "{n} closure", "{n} closures"),
              zone: String(zone),
            }),
            evidence: history.slice(0, 3).map(closureEvidence),
          }
        : labour != null
          ? {
              value: Math.round(labour * 60),
              basis: t("labour hours planned on {number}", {
                number: String(work.find((w) => w.labourHours != null)?.number),
              }),
              evidence: [],
            }
          : {
              basis: zone
                ? t("no closure of {zone} on record and no labour hours planned — not estimated", { zone })
                : t("no zone on the finding — not estimated"),
              evidence: [],
            };

    /* remote-stand penalty: the capacity model on the window the page priced */
    const cc = zone ? ctx.closureCosts[zone] : undefined;
    const remoteStand: BriefItem["remoteStand"] =
      cc && cc.known
        ? {
            bussed: cc.bussed,
            stranded: cc.stranded,
            movements: cc.movements,
            basis: t(
              "{movements} in a {minutes}-minute closure tomorrow at the usual hour: {absorbed} absorbed, {bussed} bussed to remote stands, {stranded} stranded{assumption}",
              {
                movements: tn(cc.movements, "{n} movement", "{n} movements"),
                minutes: closureMinutes.value ?? 120,
                absorbed: cc.absorbed,
                bussed: cc.bussed,
                stranded: cc.stranded,
                assumption: cc.assumption ? ` — ${cc.assumption}` : "",
              },
            ),
          }
        : cc
          ? {
              basis: /no position/i.test(cc.whyNot ?? "")
                ? t(
                    "{zone} isn't a stand or gate, so no remote-stand penalty is modeled; only closure minutes are costed",
                    { zone: String(zone) },
                  )
                : t("the capacity model could not price it: {why}", { why: cc.whyNot ?? t("no schedule coverage") }),
            }
          : {
              basis: zone
                ? t("not priced — no capacity model answer for the zone")
                : t("not priced — no zone on the finding"),
            };

    const pct = f.prediction.confidence != null ? Math.round(f.prediction.confidence * 100) : undefined;
    const level: BriefItem["confidence"]["level"] =
      f.phase === "now"
        ? "high"
        : pct != null
          ? pct >= 80
            ? "high"
            : pct >= 60
              ? "medium"
              : "low"
          : f.defensibility.grade === "strong"
            ? "high"
            : f.defensibility.grade === "partial"
              ? "medium"
              : "low";
    const why =
      f.phase === "now"
        ? t("already happening on the platform's own record")
        : pct != null
          ? t("the rule's own confidence is {pct}%; {grade} on the evidence", {
              pct,
              grade: t(f.defensibility.grade),
            })
          : t("{grade} on the evidence: {detail}", {
              grade: t(f.defensibility.grade),
              detail: f.defensibility.caveats[0] ?? f.defensibility.reasons[0] ?? t("see the intelligence report"),
            });

    return {
      rank: i + 1,
      findingId: f.id,
      headline: f.headline,
      zone,
      horizon: f.horizon.text,
      horizonDays: f.horizon.days,
      confidence: { level, pct, why },
      closureMinutes,
      remoteStand,
      decision: decisionFor(f, work, ctx.now),
      evidence: [...f.prediction.evidence, ...f.attribution.evidence]
        .filter((e, i, all) => all.findIndex((x) => x.kind === e.kind && x.ref === e.ref) === i)
        .slice(0, 6),
    };
  });

  const notes: string[] = [];
  if (candidates.length < 3)
    notes.push(
      t("Only {findings} affect capacity in the next 30 days.", {
        findings: tn(candidates.length, "{n} finding", "{n} findings"),
      }),
    );
  if (items.some((x) => x.closureMinutes.value == null))
    notes.push(
      t(
        "A blank closure-minutes cell means the zone has no closure history and no labor hours planned.",
      ),
    );
  if (items.some((x) => x.remoteStand.bussed == null))
    notes.push(t("A remote-stand cell left blank means the capacity model had no schedule coverage for the window."));

  return {
    /* the local day, not the UTC one: a brief generated at 02:00 in Madinah is that morning's brief */
    date: localDay(ctx.now),
    generatedAt: ctx.now.toISOString(),
    siteCode: report.siteCode,
    items,
    considered: candidates.length,
    notes,
  };
}

/* ── the question layer ───────────────────────────────────────────────── */

export interface ParsedQuestion {
  zone?: string;
  /** what the person typed for the zone, before it was resolved */
  zoneText?: string;
  from?: Date;
  to?: Date;
  whenText?: string;
  claimedMinutes?: number;
  wantsCost: boolean;
  wantsWhy: boolean;
}

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];
const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  half: 0.5,
};

function dayRange(d: Date): [Date, Date] {
  const from = new Date(d);
  from.setHours(0, 0, 0, 0);
  return [from, new Date(from.getTime() + DAY)];
}

export function parseEvidenceQuestion(q: string, now: Date, knownZones: string[] = []): ParsedQuestion {
  const text = q.toLowerCase();
  const out: ParsedQuestion = {
    wantsCost: /\b(cost|costs|price|penalt|minutes|how much|what did it)\b/.test(text),
    wantsWhy: /\b(why|reason|because|what happened)\b/.test(text),
  };

  /* the zone */
  let token: string | undefined;
  let m = text.match(/\b(?:rwy|runway)\s*-?\s*(\d{2})(?:\s*[/-]\s*(\d{2}))?/);
  if (m) {
    token = m[2] ? `RWY-${m[1]}-${m[2]}` : `RWY-${m[1]}`;
    out.zoneText = m[0].trim();
  } else if ((m = text.match(/\b(?:twy|taxiway)\s*-?\s*([a-z]\d?)\b/))) {
    token = `TWY-${m[1].toUpperCase()}`;
    out.zoneText = m[0].trim();
  } else if ((m = text.match(/\b(?:stand|gate|bridge|pbb)\s*-?\s*([a-z]?\d{1,3})\b/))) {
    token = m[1].toUpperCase();
    out.zoneText = m[0].trim();
  } else if ((m = text.match(/\bapron\s*-?\s*(\w+)/))) {
    token = `APRON-${m[1].toUpperCase()}`;
    out.zoneText = m[0].trim();
  } else if ((m = q.match(/\b([A-Z]{2,5}-[A-Z0-9]+(?:-[A-Z0-9]+)*)\b/))) {
    token = m[1];
    out.zoneText = m[1];
  }
  if (token) {
    const exact = knownZones.find((z) => z.toUpperCase() === token);
    const prefix = knownZones.find((z) => z.toUpperCase().startsWith(token + "-") || z.toUpperCase().startsWith(token));
    const contains = knownZones.find((z) => z.toUpperCase().includes(token));
    out.zone = exact ?? prefix ?? contains ?? token;
  }

  /* when */
  const wd = text.match(/\b(?:last|this|on)\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  const dateWords =
    text.match(
      /\b(\d{1,2})(?:st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|october|november|december)\b/,
    ) ??
    text.match(
      /\b(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})\b/,
    );
  if (wd) {
    const target = WEEKDAYS.indexOf(wd[1]);
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    const back = (d.getDay() - target + 7) % 7 || (wd[0].startsWith("this") ? 0 : 7);
    d.setDate(d.getDate() - back);
    [out.from, out.to] = dayRange(d);
    out.whenText = wd[0];
  } else if (dateWords) {
    const dayNum = Number(/^\d/.test(dateWords[1]) ? dateWords[1] : dateWords[2]);
    const monthName = /^\d/.test(dateWords[1]) ? dateWords[2] : dateWords[1];
    const d = new Date(now.getFullYear(), MONTHS.indexOf(monthName), dayNum);
    if (d.getTime() > now.getTime()) d.setFullYear(d.getFullYear() - 1);
    [out.from, out.to] = dayRange(d);
    out.whenText = dateWords[0];
  } else if (/\byesterday\b/.test(text)) {
    [out.from, out.to] = dayRange(new Date(now.getTime() - DAY));
    out.whenText = t("yesterday");
  } else if (/\btoday\b/.test(text)) {
    [out.from, out.to] = dayRange(now);
    out.whenText = t("today");
  } else if (/\b(last|past|this) (week|7 days)\b/.test(text)) {
    out.from = new Date(now.getTime() - 7 * DAY);
    out.to = now;
    out.whenText = t("the last seven days");
  } else if (/\b(last|past|this) (month|30 days)\b/.test(text)) {
    out.from = new Date(now.getTime() - 30 * DAY);
    out.to = now;
    out.whenText = t("the last thirty days");
  }

  /* a duration the person asserted, to check against the record */
  const dur = text.match(
    /\b(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|half)\s+(?:an?\s+)?(hours?|hrs?|minutes?|mins?)\b/,
  );
  if (dur) {
    const n = NUMBER_WORDS[dur[1]] ?? Number(dur[1]);
    out.claimedMinutes = Math.round(dur[2].startsWith("h") ? n * 60 : n);
  }
  return out;
}

export interface Attachment {
  kind: "closure" | "inspection" | "work_order" | "photo" | "clause" | "alert";
  ref: string;
  label: string;
  detail?: string;
  href?: string;
  /** a photograph, fetched by the page */
  objectId?: string;
  /** the record the photograph belongs to */
  of?: string;
}

export interface EvidenceAnswer {
  question: string;
  found: boolean;
  headline: string;
  happened: string[];
  why: string[];
  cost: string[];
  attachments: Attachment[];
  /** for the page: the closure window to put to the capacity model */
  costWindow?: { positionCode: string; from: string; to: string };
  /** for the page: work orders whose photographs to fetch */
  workOrderIds: string[];
  notFound?: string;
}

export interface AnswerContext {
  now: Date;
  closures: Closure[];
  workOrders: WorkOrder[];
  inspections: Inspection[];
  alerts: Alert[];
  explanations: Record<string, AlertExplanation | undefined>;
  rules: Rule[];
}

const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const dayName = (iso: string) =>
  new Date(iso).toLocaleDateString(locale() ?? "en-GB", { weekday: "long", day: "numeric", month: "long" });
const minutesText = (m: number) =>
  m >= 60 ? `${Math.floor(m / 60)} h ${m % 60 ? `${m % 60} min` : ""}`.trim() : `${m} min`;

export function answerFromRecords(question: string, p: ParsedQuestion, ctx: AnswerContext): EvidenceAnswer {
  const empty: EvidenceAnswer = {
    question,
    found: false,
    headline: "",
    happened: [],
    why: [],
    cost: [],
    attachments: [],
    workOrderIds: [],
  };
  if (!p.zone)
    return {
      ...empty,
      notFound: t("No runway, taxiway, stand or apron in the question that the records could be searched for."),
    };
  const zone = p.zone;
  const ofZone = ctx.closures.filter((c) => sameZone(c.zoneCode, zone));
  if (ofZone.length === 0) return { ...empty, notFound: t("No closure of {zone} is on record at all.", { zone }) };

  let matched: Closure[];
  let whenLabel: string;
  if (p.from && p.to) {
    matched = ofZone.filter(
      (c) =>
        new Date(c.startedAt).getTime() < p.to!.getTime() &&
        new Date(c.endedAt ?? c.startedAt).getTime() >= p.from!.getTime(),
    );
    whenLabel =
      p.whenText ??
      t("{from} to {to}", { from: p.from.toISOString().slice(0, 10), to: p.to.toISOString().slice(0, 10) });
    if (matched.length === 0) {
      const latest = [...ofZone].sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
      return {
        ...empty,
        notFound: t(
          "No closure of {zone} is on record for {when}. The most recent was {day}: {reason} ({minutes} min).",
          {
            zone,
            when: whenLabel,
            day: dayName(latest.startedAt),
            reason: latest.reason,
            minutes: latest.minutes ?? "?",
          },
        ),
      };
    }
  } else {
    const latest = [...ofZone].sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
    const [from, to] = dayRange(new Date(latest.startedAt));
    matched = ofZone.filter(
      (c) => new Date(c.startedAt).getTime() >= from.getTime() && new Date(c.startedAt).getTime() < to.getTime(),
    );
    whenLabel = t("the most recent day it was closed, {day}", { day: dayName(latest.startedAt) });
  }
  matched.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const total = matched.reduce((t, c) => t + (c.minutes ?? 0), 0);
  const planned = matched.filter((c) => c.kind === "planned").reduce((t, c) => t + (c.minutes ?? 0), 0);

  const happened: string[] = [];
  happened.push(
    t("{zone} was closed {times} on {day}, {total} in all{planned}.", {
      zone,
      times: matched.length === 1 ? t("once") : t("{n} times", { n: matched.length }),
      day: dayName(matched[0].startedAt),
      total: minutesText(total),
      planned: planned ? t(" ({minutes} of it planned)", { minutes: minutesText(planned) }) : "",
    }),
  );
  for (const c of matched)
    happened.push(
      t("{kind} {extent} closure {from}–{to}{minutes}: {reason}{chainage}.", {
        kind: c.kind === "planned" ? t("Planned") : t("Unplanned"),
        extent: c.extent,
        from: hhmm(c.startedAt),
        to: c.endedAt ? hhmm(c.endedAt) : t("open"),
        minutes: c.minutes != null ? ` (${minutesText(c.minutes)})` : "",
        reason: c.reason,
        chainage: c.fromChainageM != null && c.toChainageM != null ? `, CH ${c.fromChainageM}–${c.toChainageM}` : "",
      }),
    );
  if (p.claimedMinutes != null && total > 0 && Math.abs(p.claimedMinutes - total) > Math.max(15, total * 0.15))
    happened.push(
      t("You said {claimed}; the record says {recorded}.", {
        claimed: minutesText(p.claimedMinutes),
        recorded: minutesText(total),
      }),
    );

  const attachments: Attachment[] = matched.map((c) => ({
    kind: "closure",
    ref: c.reference ?? c.id.slice(0, 8),
    label: t("{kind} {extent} closure of {zone}", { kind: c.kind, extent: c.extent, zone: String(c.zoneCode) }),
    detail: `${c.startedAt.slice(0, 16).replace("T", " ")} · ${c.minutes ?? "?"} min · ${c.reason}`,
  }));
  const why: string[] = [];
  const cost: string[] = [];
  const workOrderIds: string[] = [];
  const dayStart = new Date(matched[0].startedAt).getTime() - DAY;
  const dayEnd = new Date(matched[matched.length - 1].endedAt ?? matched[matched.length - 1].startedAt).getTime() + DAY;

  /* the work: what the closure was declared for, then anything on the zone around then */
  const linked = matched
    .map(
      (c) =>
        c.workOrder &&
        (ctx.workOrders.find((w) => w.id === c.workOrder!.id || w.number === c.workOrder!.number) ?? {
          id: c.workOrder.id,
          number: c.workOrder.number ?? c.workOrder.id,
          title: c.workOrder.title ?? "",
        }),
    )
    .filter((w): w is WorkOrder => !!w);
  const around = ctx.workOrders.filter(
    (w) =>
      !linked.some((l) => l.id === w.id) &&
      w.zoneCode?.toUpperCase() === zone.toUpperCase() &&
      new Date(w.openedAt).getTime() <= dayEnd &&
      (isOpen(w.status) || new Date(w.completedAt ?? w.verifiedAt ?? w.openedAt).getTime() >= dayStart),
  );
  let spend = 0;
  for (const w of [...linked, ...around.slice(0, 3)]) {
    const full = ctx.workOrders.find((x) => x.id === w.id) ?? w;
    why.push(
      `${full.number}${full.title ? ` — ${full.title}` : ""}: ${full.status?.replace("_", " ") ?? t("on record")}${full.crew ? `, ${full.crew}` : full.discipline ? t(", {discipline} discipline", { discipline: full.discipline }) : ""}${full.completedAt ? t(", completed {date}", { date: full.completedAt.slice(0, 10) }) : ""}${full.costActual != null ? t(", cost {amount}", { amount: money(full.costActual, "SAR") }) : ""}.`,
    );
    if (full.costActual != null) spend += full.costActual;
    attachments.push({
      kind: "work_order",
      ref: full.number,
      label: full.title ?? full.number,
      detail: `${full.status ?? ""}${full.crew ? ` · ${full.crew}` : ""}`,
      href: `/work-orders/${full.id}`,
    });
    workOrderIds.push(full.id);
  }

  /* the inspection that found it, or checked it after */
  const insp = ctx.inspections.filter(
    (i) =>
      i.zoneCode?.toUpperCase() === zone.toUpperCase() &&
      i.completedAt &&
      new Date(i.completedAt).getTime() >= dayStart - DAY &&
      new Date(i.completedAt).getTime() <= dayEnd + DAY,
  );
  for (const i of insp.slice(0, 3)) {
    why.push(
      t("Inspection {name} completed {date}: score {score}, {failed}.", {
        name: i.templateName,
        date: i.completedAt!.slice(0, 16).replace("T", " "),
        score: i.score ?? "—",
        failed: tn(i.failures, "{n} failed item", "{n} failed items"),
      }),
    );
    attachments.push({
      kind: "inspection",
      ref: i.id.slice(0, 8),
      label: i.templateName,
      detail: t("{date} · {failed} failed · {answered} answered", {
        date: i.completedAt!.slice(0, 10),
        failed: i.failures,
        answered: i.answers,
      }),
      href: `/inspections/${i.id}`,
    });
  }

  /* the rule that fired, and the clause it carries */
  const rulesByKey = new Map(ctx.rules.map((r) => [r.key, r]));
  /* Which rules already have a clause on the answer, so the closure's own
     rule below does not add a second row for the same regulation. */
  const clauseRules = new Set<string>();
  const fired = ctx.alerts.filter(
    (a) =>
      a.zoneCode?.toUpperCase() === zone.toUpperCase() &&
      new Date(a.raisedAt).getTime() >= dayStart - 7 * DAY &&
      new Date(a.raisedAt).getTime() <= dayEnd,
  );
  for (const a of fired.slice(0, 3)) {
    const x = ctx.explanations[a.id];
    const rule = a.ruleKey ? rulesByKey.get(a.ruleKey) : undefined;
    const ref = x?.reference ?? rule?.reference;
    why.push(
      t('Rule "{rule}" fired {date}: {title}{ref}.', {
        rule: x?.ruleName ?? rule?.name ?? a.ruleKey ?? "unnamed",
        date: a.raisedAt.slice(0, 10),
        title: a.title,
        ref: ref ? ` — ${ref}` : "",
      }),
    );
    attachments.push({
      kind: "alert",
      ref: a.assetTag ?? a.zoneCode ?? a.id.slice(0, 8),
      label: a.title,
      detail: t("severity {severity} · {date}", { severity: a.severity, date: a.raisedAt.slice(0, 10) }),
      href: `/alerts?open=${a.id}`,
    });
    if (ref) {
      if (a.ruleKey) clauseRules.add(a.ruleKey);
      attachments.push({
        kind: "clause",
        ref,
        label: t('Clause behind "{rule}"', { rule: String(x?.ruleName ?? rule?.name ?? a.ruleKey) }),
        detail: x?.expression,
      });
    }
  }

  /*
   * The clause behind the closure itself.
   *
   * A closure carries `reasonCode` — the key of the rule it was declared
   * under — so the regulation that stands behind it is a link in the record,
   * not something to be inferred. The loop above inferred it: it found the
   * clause only when an alert for that zone happened to have been raised
   * within seven days of the closure, which is a coincidence standing in for
   * a fact the closure already states.
   *
   * It drifted, and silently. The sample closures are placed relative to
   * today so that "last Tuesday" is always a real day, while the sample
   * alerts hang off a fixed date — so a week after that date the friction
   * alert fell out of the window and the GACAR clause simply stopped
   * appearing under an answer that still looked complete. An evidence chain
   * that loses its regulatory reference and says nothing about it is worse
   * than one that never had it: "why did we close the runway" is a question
   * asked about a closure from any date, and the answer must not depend on
   * how near an alert happened to fall.
   */
  for (const c of matched) {
    const rule = c.reasonCode ? rulesByKey.get(c.reasonCode) : undefined;
    if (!rule?.reference || clauseRules.has(rule.key)) continue;
    clauseRules.add(rule.key);
    attachments.push({
      kind: "clause",
      ref: rule.reference,
      label: t('Clause behind "{rule}"', { rule: rule.name }),
      detail: rule.confirmedAgainst,
    });
  }
  if (why.length === 0)
    why.push(
      matched.length === 1
        ? t(
            "The closure records give the reason above; no work order, inspection or rule on the platform is tied to {zone} around that day.",
            { zone },
          )
        : t(
            "The closure records give the reasons above; no work order, inspection or rule on the platform is tied to {zone} around that day.",
            { zone },
          ),
    );

  /* the cost */
  cost.push(
    t("{total} of closure{planned}.", {
      total: minutesText(total),
      planned: planned ? t(", {minutes} planned", { minutes: minutesText(planned) }) : "",
    }),
  );
  if (spend > 0)
    cost.push(
      linked.length + around.length === 1
        ? t("{amount} actual cost on the work order.", { amount: money(spend, "SAR") })
        : t("{amount} actual cost on the work orders.", { amount: money(spend, "SAR") }),
    );
  const first = matched[0];
  const last = matched[matched.length - 1];

  return {
    question,
    found: true,
    headline: t("{zone}, {day}: {total} closed", { zone, day: dayName(first.startedAt), total: minutesText(total) }),
    happened,
    why,
    cost,
    attachments,
    costWindow: { positionCode: zone, from: first.startedAt, to: last.endedAt ?? last.startedAt },
    workOrderIds,
  };
}

/** The cost lines the capacity model adds, once the page has asked it. */
export function costLinesFrom(cc: ClosureCost | null): string[] {
  if (!cc) return [t("The capacity model did not answer for that window.")];
  if (!cc.known)
    return [
      t("The capacity model could not price the window: {why}.", { why: cc.whyNot ?? t("no schedule coverage") }),
    ];
  return [
    t(
      "{movements} fell in the window: {absorbed} absorbed elsewhere, {bussed} bussed to remote stands, {stranded} stranded{undetermined}.",
      {
        movements: tn(cc.movements, "{n} movement", "{n} movements"),
        absorbed: cc.absorbed,
        bussed: cc.bussed,
        stranded: cc.stranded,
        undetermined: cc.undetermined ? t(", {n} undetermined", { n: cc.undetermined }) : "",
      },
    ),
    t("Remote-stand penalty: {bussed}{assumption}.", {
      bussed: tn(cc.bussed, "{n} bussed movement", "{n} bussed movements"),
      assumption: cc.assumption ? ` — ${cc.assumption}` : "",
    }),
  ];
}
